import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    InputAdornment,
    IconButton,
    Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import themeColors from "../theme";

const toInputValue = (value) =>
    value === null || value === undefined || value === "" ? "" : String(value);

/**
 * First-time orientation walkthrough for the Fund Tracker.
 *
 * A 3-step tour that walks the user through the real first setup flow:
 *   1. Enter their annual CME allowance
 *   2. Enter the rollover amount (0 if not allowed)
 *   3. Select a category for their first expense
 *
 * The budget values are saved by the parent page, and the final category opens
 * the real expense form. Each step is a centered modal over a dimmed backdrop.
 * On step 1 the Fund Settings card is spotlighted (via `spotlightRect`, a
 * DOMRect supplied by the parent).
 */
export default function FundTrackerTour({
    stepIndex,
    categoryOptions = [],
    initialFund = "",
    initialRollover = "",
    spotlightRect,
    onNext,
    onClose,
    onSaveFund,
    onSaveRollover,
    onSelectCategory,
}) {
    const { teal } = themeColors;
    const totalSteps = 3;
    const isLast = stepIndex === totalSteps - 1;

    const [fund, setFund] = useState(() => toInputValue(initialFund));
    const [rollover, setRollover] = useState(() => toInputValue(initialRollover));
    const [category, setCategory] = useState("");
    const [fundEdited, setFundEdited] = useState(false);
    const [rolloverEdited, setRolloverEdited] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!fundEdited) setFund(toInputValue(initialFund));
    }, [fundEdited, initialFund]);

    useEffect(() => {
        if (!rolloverEdited) setRollover(toInputValue(initialRollover));
    }, [rolloverEdited, initialRollover]);

    const isNonNegativeAmount = (value) => {
        if (value === "") return false;
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0;
    };

    const getErrorMessage = (err) =>
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to save this step. Please try again.";

    const handleContinue = async () => {
        setError("");

        if (stepIndex === 0 && !isNonNegativeAmount(fund)) {
            setError("Please enter your annual CME fund amount.");
            return;
        }

        if (stepIndex === 1 && !isNonNegativeAmount(rollover)) {
            setError("Please enter a rollover amount. Use 0 if rollover is not allowed.");
            return;
        }

        if (stepIndex === 2 && !category) {
            setError("Please select a category to continue.");
            return;
        }

        if (stepIndex === 2) {
            try {
                onSelectCategory?.(category);
            } catch (err) {
                setError(getErrorMessage(err));
            }
            return;
        }

        setSaving(true);
        try {
            if (stepIndex === 0) {
                await onSaveFund?.(Number(fund));
                onNext();
            } else {
                await onSaveRollover?.(Number(rollover));
                onNext();
            }
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    // Spotlight cutout (step 1 only). Pad slightly so the card breathes.
    const pad = 8;
    const rect = spotlightRect
        ? {
              top: spotlightRect.top - pad,
              left: spotlightRect.left - pad,
              width: spotlightRect.width + pad * 2,
              height: spotlightRect.height + pad * 2,
          }
        : null;

    return (
        <>
            {/* Backdrop: spotlight cutout over Fund Settings (step 1) or a plain dim. */}
            {rect ? (
                <Box
                    sx={{
                        position: "fixed",
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                        borderRadius: "12px",
                        border: `2px solid ${teal}`,
                        backgroundColor: "rgba(0, 127, 126, 0.12)",
                        boxShadow: "0 0 0 9999px rgba(0,0,0,0.55), 0 0 24px rgba(0,127,126,0.6)",
                        pointerEvents: "none",
                        zIndex: 1300,
                        transition: "all 0.25s ease",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.55)",
                        zIndex: 1300,
                    }}
                />
            )}

            {/* Centered orientation card */}
            <Box
                sx={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 440,
                    maxWidth: "calc(100vw - 32px)",
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                    p: 4,
                    zIndex: 1400,
                }}
            >
                <IconButton
                    aria-label="Skip tour"
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8, color: "#9E9E9E" }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography variant="h6" fontWeight="bold" color="#2C3E50" sx={{ mb: 2 }}>
                    Orientation:{" "}
                    <Box component="span" sx={{ color: "#6C757D", fontWeight: 600 }}>
                        Step {stepIndex + 1} of {totalSteps}
                    </Box>
                </Typography>

                {stepIndex === 0 && (
                    <>
                        <Typography variant="body1" color="#2C3E50" sx={{ mb: 3 }}>
                            Welcome to your personal CME Fund Tracker. Please enter your total
                            CME allowance for the year.
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="Enter amount"
                            value={fund}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e") e.preventDefault();
                            }}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === "" || parseFloat(v) >= 0) {
                                    setFundEdited(true);
                                    setFund(v);
                                }
                            }}
                            slotProps={{
                                htmlInput: { min: 0 },
                                input: {
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                },
                            }}
                        />
                    </>
                )}

                {stepIndex === 1 && (
                    <>
                        <Typography variant="body1" color="#2C3E50" sx={{ mb: 3 }}>
                            Please enter the amount of CME funds that rollover to the next year.
                            If rollover is not allowed, please enter 0.
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="0.00"
                            value={rollover}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e") e.preventDefault();
                            }}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === "" || parseFloat(v) >= 0) {
                                    setRolloverEdited(true);
                                    setRollover(v);
                                }
                            }}
                            slotProps={{
                                htmlInput: { min: 0 },
                                input: {
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                },
                            }}
                        />
                    </>
                )}

                {stepIndex === 2 && (
                    <>
                        <Typography variant="body1" color="#2C3E50" sx={{ mb: 3 }}>
                            Start by selecting a category for your first CME purchase.
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            label="Category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            SelectProps={{
                                displayEmpty: true,
                                // The orientation card sits at zIndex 1400; the menu must
                                // render above it, otherwise the option list opens behind
                                // the card and only the top/bottom items are visible.
                                MenuProps: { sx: { zIndex: 1500 } },
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        >
                            <MenuItem value="" disabled>
                                Select a Category...
                            </MenuItem>
                            {categoryOptions.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </TextField>
                    </>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleContinue}
                    disabled={saving}
                    sx={{
                        mt: 3,
                        backgroundColor: teal,
                        textTransform: "none",
                        borderRadius: "8px",
                        py: 1.25,
                        fontWeight: 600,
                        "&:hover": { backgroundColor: "#005F5E" },
                    }}
                >
                    {saving ? "Saving..." : isLast ? "Continue" : "Save and Continue"}
                </Button>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Link
                        component="button"
                        onClick={onClose}
                        sx={{ color: teal, textDecoration: "underline", fontWeight: 500 }}
                    >
                        Skip Tour
                    </Link>
                </Box>
            </Box>
        </>
    );
}
